import React, { useEffect, useState } from 'react';
import Block from '@legacy-vfuk/core-block';
import RichTextElement from '../../Elements/RichText';
import {
  SectionDynaimcShelfProps,
  SectionFeaturedShelfProps,
  SectionKcRibbonProps,
} from './types/SectionKcRibbon.types';
import { Icon } from '@legacy-vfuk/core-icon';
import { ReactComponent as EntertainmentCelebrateIcon } from '../../../../images/icon/entertainment-celebrate.svg';
import {
  BlockWrapper,
  whiteCSS,
  galleryCSS,
  ContainerWrapper,
  ContentWrapper,
  ArticlesWrapper,
  FeaturedCard,
  LatestCard,
  MostPopularCard,
  MediaCard,
  RecentlyViewedCard,
  SixthCard,
  TopicPreferencesCard,
  TopicWrapper,
  Topic,
  TopicText,
  TopicTitle,
  TopicButtonWrapper,
  ButtonRowWrapper,
} from './styles/SectionKcRibbon.styles';
import { compareArticlesByViewCount } from '../../../../helpers/articleHelpers';
import { ArticleCard } from '../../Elements/ArticleCard';
import CtaButtonRow from '../../Elements/CtaButtons';

const SectionKcRibbon = ({
  backgroundColor,
  titleContent,
  articles,
  articleShelves,
  buttonRow,
  preferences = [],
  saveLabelText,
  saveIconClicked,
  indexSlug,
}: SectionKcRibbonProps) => {
  const [recentlyViewedShelfArticles, setRecentlyViewedShelfArticles] =
    useState([]);
  const backgroundCss = backgroundColor === 'white' ? whiteCSS : galleryCSS;

  useEffect(() => {
    const setupRecentlyViewedArticles = () => {
      const slugs =
        JSON.parse(window.localStorage.getItem('viewed-articles') || '[]') ||
        [];
      const recentArticles = slugs
        .slice(0, 4)
        .map((slug: string) => articles.find(article => article.slug === slug))
        .filter(Boolean);
      setRecentlyViewedShelfArticles(recentArticles);
    };
    if (typeof window !== 'undefined') setupRecentlyViewedArticles();
  }, [articles]);

  const setupArticleShelves = (
    articleShelves: (SectionFeaturedShelfProps | SectionDynaimcShelfProps)[]
  ) => {
    const updatedArticleShelves: (
      | SectionFeaturedShelfProps
      | SectionDynaimcShelfProps
    )[] = [];

    const rececentlyViewedSlugs = new Set(
      recentlyViewedShelfArticles.map(article => article.slug)
    );

    const mediaArticles = articles
      .filter(article => !rececentlyViewedSlugs.has(article.slug))
      .filter(article => {
        return article.contentType?.isMedia === true;
      })
      .slice(0, 3);

    const mediaSlugs = new Set(mediaArticles.map(article => article.slug));

    const mostPopularArticles = articles
      .filter(article => !rececentlyViewedSlugs.has(article.slug))
      .filter(article => !mediaSlugs.has(article.slug))
      .sort(compareArticlesByViewCount)
      .slice(0, 3);
    const mostPopularSlugs = new Set(
      mostPopularArticles.map(article => article.slug)
    );

    const latestArticles = articles
      .filter(article => !rececentlyViewedSlugs.has(article.slug))
      .filter(article => !mediaSlugs.has(article.slug))
      .filter(article => !mostPopularSlugs.has(article.slug))
      .sort((a, b) => {
        return (
          new Date(b.publishingDate).getTime() -
          new Date(a.publishingDate).getTime()
        );
      })
      .slice(0, 10);

    for (const articleShelf of articleShelves) {
      if (
        articleShelf.__typename === 'ContentfulSectionFeaturedShelf' &&
        articleShelf.articles &&
        articleShelf.articles.length > 0
      ) {
        updatedArticleShelves.push(articleShelf);
      } else if (articleShelf.__typename === 'ContentfulSectionDynamicShelf') {
        switch ((articleShelf as SectionDynaimcShelfProps).type) {
          case 'latest':
            articleShelf.articles = latestArticles;
            (articleShelf as SectionDynaimcShelfProps).displaySeeAllLabel =
              true;
            break;
          case 'most-popular':
            articleShelf.articles = mostPopularArticles;
            (articleShelf as SectionDynaimcShelfProps).displaySeeAllLabel =
              false;
            break;
          case 'recently-viewed':
            articleShelf.articles = recentlyViewedShelfArticles;
            (articleShelf as SectionDynaimcShelfProps).displaySeeAllLabel =
              true;
            break;
          case 'media':
            articleShelf.articles = mediaArticles;
            (articleShelf as SectionDynaimcShelfProps).displaySeeAllLabel =
              true;
            break;
          default:
            articleShelf.articles = [];
            break;
        }

        if (articleShelf.articles && articleShelf.articles.length > 0) {
          updatedArticleShelves.push(articleShelf);
        }
      }
    }
    return updatedArticleShelves;
  };

  const generateOrderedArticles = (data: any[], order: string[]) => {
    const array: any[] = [];

    order.forEach(displayName => {
      const section = data.find(s => s.displayName === displayName);
      if (section) {
        const article = section.articles.find(
          a => !array.some(r => r.id === a.id)
        );
        if (article) array.push({ ...article, displayName });
      }
    });

    const latestSection = data.find(item => item.displayName === 'Latest');
    while (array.length < 6 && latestSection) {
      const article = latestSection.articles.find(
        a => !array.some(r => r.id === a.id)
      );
      if (article) array.push({ ...article, displayName: 'Latest' });
      else break;
    }

    let latestFound = false;
    array.forEach(item => {
      if (item.displayName === 'Latest') {
        if (latestFound) {
          item.displayName = '';
        } else {
          latestFound = true;
        }
      }
    });

    return array;
  };

  const data = setupArticleShelves(articleShelves);
  const order = [
    'Featured',
    'Latest',
    'Most Popular',
    'Media',
    'Recently viewed by you',
    'Latest',
  ];
  const orderedArticles = generateOrderedArticles(data, order);

  const renderCard = (article, index) => {
    const cardTypes = [
      FeaturedCard,
      LatestCard,
      MostPopularCard,
      MediaCard,
      RecentlyViewedCard,
      SixthCard,
    ];
    const CardComponent = cardTypes[index] || SixthCard;

    return (
      <CardComponent key={`card-${index}`}>
        {index === 5 && preferences.length ? (
          <TopicPreferencesCard href={preferences[0].buttonRow.buttons[0].url}>
            <TopicTitle>
              <EntertainmentCelebrateIcon />
              <h3>{preferences[0].title}</h3>
            </TopicTitle>
            <TopicWrapper>
              {preferences[0].topics.map(topic => (
                <Topic key={topic.topicName}>
                  <Icon name="tick-outline" appearance="primary" inverse />
                  <TopicText>{topic.topicName}</TopicText>
                </Topic>
              ))}
            </TopicWrapper>
            {preferences[0].buttonRow && (
              <TopicButtonWrapper>
                <CtaButtonRow {...preferences[0].buttonRow} />
              </TopicButtonWrapper>
            )}
          </TopicPreferencesCard>
        ) : (
          <ArticleCard
            isAnimated
            isFeatured
            data={article}
            backgroundColor="white"
            indexSlug={indexSlug}
            seoTitle={article.seoTitle}
            readingTimeMinuteLabel={article.readingTime}
            displaySaveIcon
            saveLabelText={saveLabelText}
            handleSaveClick={saveIconClicked}
            label={article.displayName}
            withTagLabel={false}
          />
        )}
      </CardComponent>
    );
  };

  return (
    <BlockWrapper>
      <Block css={backgroundCss}>
        <ContainerWrapper>
          <ContentWrapper>
            {titleContent?.map(item => (
              <RichTextElement
                key={item.id}
                rawRichText={item.text.raw}
                color={item.color}
                textAlign={item.textAlign}
              />
            ))}
          </ContentWrapper>
          <ArticlesWrapper>
            {orderedArticles.map((article, index) =>
              renderCard(article, index)
            )}
          </ArticlesWrapper>
          <ButtonRowWrapper>
            <CtaButtonRow {...buttonRow} />
          </ButtonRowWrapper>
        </ContainerWrapper>
      </Block>
    </BlockWrapper>
  );
};

export default SectionKcRibbon;
